import React from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #F9FAFB;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #148D89 0%, #1C64F2 100%);
  padding: 5rem 2rem;
  color: white;
  text-align: center;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const HeroTitle = styled.h1`
  font-size: clamp(2rem, 5vw, 3rem);
  margin-bottom: 1rem;
`;

const HeroSubtitle = styled.p`
  font-size: 1.3rem;
  opacity: 0.95;
  max-width: 800px;
  margin: 0 auto;
`;

const ContentSection = styled.section`
  padding: 4rem 2rem;
  background: white;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 3rem;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const MainContent = styled.div``;

const Sidebar = styled.div`
  position: sticky;
  top: 100px;
  height: fit-content;
`;

const InfoCard = styled.div`
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  border-radius: 20px;
  padding: 2rem;
  margin-bottom: 2rem;
  
  h3 {
    color: #148D89;
    margin-bottom: 1rem;
  }
  
  p {
    margin-bottom: 1rem;
    color: #4B5563;
  }
  
  .price {
    font-size: 2rem;
    font-weight: 700;
    color: #148D89;
    margin: 1rem 0;
  }
`;


const Section = styled.div`
  margin-bottom: 3rem;
  
  h2 {
    color: #15243A;
    margin-bottom: 1.5rem;
    padding-bottom: 0.5rem;
    border-bottom: 3px solid #148D89;
  }
  
  h3 {
    color: #148D89;
    margin: 1.5rem 0 1rem;
  }
  
  ul {
    list-style: none;
    padding: 0;
    
    li {
      padding: 0.75rem 0;
      padding-left: 2rem;
      position: relative;
      color: #4B5563;
      
      &::before {
        content: '✓';
        position: absolute;
        left: 0;
        color: #148D89;
        font-weight: bold;
        font-size: 1.2rem;
      }
    }
  }
`;

const ModuleCard = styled.div`
  background: #F9FAFB;
  border-left: 4px solid #148D89;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border-radius: 10px;
  
  h4 {
    color: #15243A;
    margin-bottom: 0.5rem;
  }
  
  p {
    color: #6B7280;
    margin-bottom: 0.5rem;
  }
  
  .duration {
    font-weight: 600;
    color: #148D89;
  }
`;

const BenefitGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
`;

const BenefitCard = styled.div`
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 1.5rem;
  border-radius: 15px;
  text-align: center;
  
  .icon {
    font-size: 2rem;
    margin-bottom: 1rem;
  }
  
  h4 {
    color: #148D89;
    margin-bottom: 0.5rem;
  }
  
  p {
    color: #6B7280;
    font-size: 0.9rem;
  }
`;

const SecretaireMedical: React.FC = () => {
  return (
    <PageWrapper>
      <Helmet>
        <title>Formation Secrétaire Médicale - Institut Genevois de Formation</title>
        <meta name="description" content="Formation complète pour devenir secrétaire médicale qualifiée. Apprenez la gestion administrative, l'accueil des patients et les outils informatiques spécialisés. 100% en ligne, certification IGF." />
        <meta name="keywords" content="secrétaire médicale, formation secrétaire médicale, formation en ligne, gestion administrative médicale, certification IGF" />
      </Helmet>
      
      <HeroSection>
        <Container>
          <HeroTitle>Formation Secrétaire Médicale</HeroTitle>
          <HeroSubtitle>
            Devenez une professionnelle qualifiée de la gestion administrative médicale avec notre formation 100% en ligne
          </HeroSubtitle>
        </Container>
      </HeroSection>
      
      <ContentSection>
        <Container>
          <Grid>
            <MainContent>
              <Section>
                <h2>Présentation de la formation</h2>
                <p>
                  La formation de Secrétaire Médicale de l'Institut Genevois de Formation vous prépare à exercer un rôle clé dans le secteur médical. 
                  Cette formation complète de 12 semaines, entièrement dispensée en ligne, vous permettra d'acquérir toutes les compétences nécessaires 
                  pour gérer efficacement l'administration d'un cabinet médical ou d'un établissement de santé.
                </p>
                <p>
                  Grâce à une approche pédagogique innovante combinant vidéos pré-enregistrées, exercices pratiques et sessions en visioconférence, 
                  vous développerez une expertise reconnue dans le domaine médical.
                </p>
              </Section>
              
              <Section>
                <h2>Programme de formation</h2>
                
                <ModuleCard>
                  <h4>Module 1 : Accueil et Communication</h4>
                  <p className="duration">Semaines 1 à 4</p>
                  <p>Maîtrisez l'accueil physique et téléphonique des patients avec clarté et empathie.</p>
                  <ul>
                    <li>Techniques d'accueil professionnel</li>
                    <li>Communication empathique avec les patients</li>
                    <li>Gestion des appels téléphoniques</li>
                    <li>Prise de rendez-vous optimisée</li>
                  </ul>
                </ModuleCard>
                
                <ModuleCard>
                  <h4>Module 2 : Assistance Médicale</h4>
                  <p className="duration">Semaines 5 à 8</p>
                  <p>Apprenez à assister le praticien et à accompagner le patient dans son parcours de soins.</p>
                  <ul>
                    <li>Installation et préparation du patient</li>
                    <li>Prise des constantes vitales</li>
                    <li>Assistance aux gestes médicaux</li>
                    <li>Hygiène et stérilisation</li>
                  </ul>
                </ModuleCard>
                
                <ModuleCard>
                  <h4>Module 3 : Organisation Administrative</h4>
                  <p className="duration">Semaines 9 à 12</p>
                  <p>Maîtrisez la gestion administrative complète d'un cabinet médical.</p>
                  <ul>
                    <li>Gestion de l'agenda médical</li>
                    <li>Facturation et télétransmission</li>
                    <li>Tenue des dossiers patients</li>
                    <li>Logiciels de gestion médicale</li>
                  </ul>
                </ModuleCard>
              </Section>
              
              <Section>
                <h2>Objectifs de la formation</h2>
                <ul>
                  <li>Accueillir et orienter les patients avec professionnalisme</li>
                  <li>Gérer efficacement l'agenda et les rendez-vous médicaux</li>
                  <li>Maîtriser les outils informatiques spécifiques au secteur médical</li>
                  <li>Assurer la facturation et la télétransmission des actes médicaux</li>
                  <li>Tenir et organiser les dossiers médicaux dans le respect de la confidentialité</li>
                  <li>Communiquer efficacement avec les patients et l'équipe médicale</li>
                  <li>Respecter les normes d'hygiène et de sécurité en milieu médical</li>
                </ul>
              </Section>
              
              <Section>
                <h2>Méthode pédagogique</h2>
                <BenefitGrid>
                  <BenefitCard>
                    <div className="icon">🎥</div>
                    <h4>Vidéos pédagogiques</h4>
                    <p>Cours pré-enregistrés accessibles 24h/24</p>
                  </BenefitCard>
                  <BenefitCard>
                    <div className="icon">📝</div>
                    <h4>Exercices pratiques</h4>
                    <p>Mises en situation et cas concrets</p>
                  </BenefitCard>
                  <BenefitCard>
                    <div className="icon">💬</div>
                    <h4>Sessions en visio</h4>
                    <p>Questions, corrections et accompagnement</p>
                  </BenefitCard>
                  <BenefitCard>
                    <div className="icon">📊</div>
                    <h4>QCM interactifs</h4>
                    <p>Évaluation continue des connaissances</p>
                  </BenefitCard>
                </BenefitGrid>
              </Section>
              
              <Section>
                <h2>Débouchés professionnels</h2>
                <p>Après votre formation, vous pourrez exercer dans :</p>
                <ul>
                  <li>Cabinets médicaux généralistes ou spécialisés</li>
                  <li>Cliniques privées</li>
                  <li>Centres de santé</li>
                  <li>Hôpitaux publics ou privés</li>
                  <li>Laboratoires d'analyses médicales</li>
                  <li>Centres d'imagerie médicale</li>
                  <li>Maisons de santé pluridisciplinaires</li>
                </ul>
              </Section>
              
              <Section>
                <h2>Validation et certification</h2>
                <p>
                  La formation est validée par un examen final comprenant un QCM théorique et une pratique simulée. 
                  La réussite à cet examen vous permettra d'obtenir le diplôme de Secrétaire Médicale délivré par 
                  l'Institut Genevois de Formation, reconnu dans le secteur médical.
                </p>
                <p>
                  Notre taux de réussite de 95% témoigne de la qualité de notre accompagnement et de notre méthode pédagogique.
                </p>
              </Section>
            </MainContent>
            
            <Sidebar>
              <InfoCard>
                <h3>Informations pratiques</h3>
                <p><strong>Durée :</strong> 12 semaines</p>
                <p><strong>Format :</strong> 100% en ligne</p>
                <p><strong>Niveau requis :</strong> Bac ou équivalent</p>
                <p><strong>Certification :</strong> Diplôme IGF</p>
                <p className="price">1400€</p>
                <p><small>Possibilité de paiement échelonné</small></p>
              </InfoCard>
              
              <InfoCard>
                <h3>Prochaines sessions</h3>
                <p>📅 Janvier 2025</p>
                <p>📅 Mars 2025</p>
                <p>📅 Mai 2025</p>
                <p>📅 Septembre 2025</p>
                <p><small>Places limitées par session</small></p>
              </InfoCard>
              
              <InfoCard>
                <h3>Besoin d'informations ?</h3>
                <p>Notre équipe est à votre disposition pour répondre à toutes vos questions.</p>
                <p>📞 +33 6 01 48 53 83</p>
                <p>✉️ contact@institutgenevoisdeformation.fr</p>
              </InfoCard>
            </Sidebar>
          </Grid>
        </Container>
      </ContentSection>
    </PageWrapper>
  );
};

export default SecretaireMedical;